import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Car, Power, PowerOff, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HostListingDialog } from "@/components/host/HostListingDialog";

const money = (n) => `$${Math.round(Number(n || 0))}`;

export function HostListings({ listings, editListing, setListingAvailability, deleteListing }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const visible = query
    ? listings.filter((c) => [c.make, c.name, c.category].some((f) => (f || "").toString().toLowerCase().includes(query)))
    : listings;
  const STATUS = {
    approved: { label: t("hostListings.live"), cls: "bg-emerald-100 text-emerald-700" },
    under_review: { label: t("hostListings.underReview"), cls: "bg-amber-100 text-amber-700" },
    rejected: { label: t("hostListings.rejected"), cls: "bg-rose-100 text-rose-700" },
  };

  return (
    <div className="space-y-5" data-testid="host-listings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-slate-500 text-sm">{t("hostListings.fleetCount", { count: listings.length })}</p>
        <div className="flex items-center gap-3 flex-wrap">
          {listings.length > 1 && (
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="host-listing-search"
                placeholder="Search make or model…"
                className="border-2 border-slate-200 rounded-full pl-9 pr-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
            </div>
          )}
          <Link to="/host" data-testid="add-listing-button" className="inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-5 py-2.5 font-semibold text-sm hover:bg-blue-700 transition-colors">
            <Plus size={16} /> {t("hostListings.addVehicle")}
          </Link>
        </div>
      </div>

      {listings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="listings-empty">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-blue-50 text-blue-500 mx-auto"><Car size={26} /></span>
          <p className="text-slate-500 mt-4 font-medium">{t("hostListings.empty")}</p>
          <p className="text-slate-400 text-sm mt-1">{t("hostListings.emptySub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {visible.map((car) => {
            const s = STATUS[car.review_status] || STATUS.under_review;
            const paused = car.review_status === "approved" && car.available === false;
            return (
              <div key={car.id} data-testid={`listing-card-${car.id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex">
                <div className="w-32 sm:w-40 shrink-0 bg-slate-100 relative">
                  {car.image_url ? <img src={car.image_url} alt={car.name} className="w-full h-full object-cover" /> : <span className="absolute inset-0 grid place-items-center text-slate-300"><Car size={28} /></span>}
                </div>
                <div className="flex-1 p-4 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading font-semibold text-slate-900 truncate">{car.name}</h3>
                    <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{paused ? t("hostListings.paused") : s.label}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{t("hostListings.perDaySeats", { rate: Math.round(car.rates?.daily_start || car.weekly_rate / 7), seats: car.seats, transmission: car.transmission })}</p>
                  {car.review_status === "rejected" && car.rejection_reason && (
                    <p className="text-xs text-rose-600 mt-1.5">{t("hostListings.reason", { reason: car.rejection_reason })}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 pt-3 border-t border-slate-100 text-sm">
                    <button onClick={() => setEditing(car)} data-testid={`edit-listing-${car.id}`} className="font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5"><Pencil size={14} /> {t("hostListings.edit")}</button>
                    {car.review_status === "approved" && (
                      <button onClick={() => setListingAvailability(car.id, car.available === false)} data-testid={`toggle-listing-${car.id}`} className="font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                        {car.available === false ? <><Power size={14} /> {t("hostListings.resume")}</> : <><PowerOff size={14} /> {t("hostListings.pause")}</>}
                      </button>
                    )}
                    <button onClick={() => setConfirmDel(car)} data-testid={`delete-listing-${car.id}`} className="font-semibold text-rose-500 hover:text-rose-600 inline-flex items-center gap-1.5"><Trash2 size={14} /> {t("hostListings.remove")}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <HostListingDialog car={editing} onClose={() => setEditing(null)} onSave={editListing} />}

      {confirmDel && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
            <h3 className="font-heading text-lg font-bold text-slate-900">{t("hostListings.removeTitle")}</h3>
            <p className="text-slate-500 text-sm mt-2">{t("hostListings.removeConfirm", { name: confirmDel.name })}</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmDel(null)} className="rounded-full bg-white border border-slate-200 text-slate-700 px-5 py-2.5 font-semibold text-sm hover:bg-slate-50">{t("hostListings.cancel")}</button>
              <button onClick={() => { deleteListing(confirmDel.id); setConfirmDel(null); }} data-testid="delete-confirm-button" className="rounded-full bg-rose-600 text-white px-5 py-2.5 font-semibold text-sm hover:bg-rose-700">{t("hostListings.remove")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
